import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Users, MapPin, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';

const HandpanWorkshop = () => {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  const handleGoBack = () => {
    navigate('/#upcoming-experiences');
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Handpan Workshop - Meditative Music in Nature",
    "description": "Discover the meditative magic of handpan music in a forest setting. 4-hour workshop for all levels with expert instruction.",
    "startDate": "2024-04-20T14:00",
    "endDate": "2024-04-20T18:00",
    "location": {
      "@type": "Place",
      "name": "Forest Sound Circle",
      "address": "Beskid Wyspowy, Poland"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Mystic Forest",
      "url": "https://mysticforest.club"
    },
    "offers": {
      "@type": "Offer",
      "price": "125",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <SEO
        title="Handpan Workshop - Music as Meditation in Nature"
        description="4-hour handpan workshop in the forest. Learn to play this meditative instrument with expert instruction. All levels welcome."
        canonicalUrl="https://mysticforest.club/experiences/handpan-workshop"
        jsonLd={jsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
      
      {/* Go Back Button */}
      <div className="pt-20 pb-4">
        <div className="max-w-6xl mx-auto px-6">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Experiences
          </Button>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative py-32">
        <div className="absolute inset-0">
          <img 
            src="/lovable-uploads/4af51cf9-c4d1-4e73-81fe-2f922b6fed8b.png" 
            alt="Musician playing handpan instrument in peaceful forest setting"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
            Handpan Workshop
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Discover the meditative magic of handpan music in nature
          </p>
          <Button size="lg" className="px-8 py-4 text-lg" onClick={handleBooking}>
            Play Your Heart
          </Button>
        </div>
      </section>

      {/* Quick Info */}
      <section className="py-16 bg-card">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Calendar className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">When</h3>
              <p className="text-muted-foreground">April 20, 2024</p>
              <p className="text-sm text-muted-foreground">2 PM - 6 PM</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Where</h3>
              <p className="text-muted-foreground">Forest Sound Circle</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">For Whom</h3>
              <p className="text-muted-foreground">Music lovers, all levels</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Duration</h3>
              <p className="text-muted-foreground">4 Hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-8 text-center">
            Where Music Meets Meditation
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground">
            <p className="mb-6">
              The handpan is a relatively new instrument that creates otherworldly, meditative sounds that seem to emerge from another dimension. Its ethereal tones naturally induce states of calm and wonder, making it perfect for both musical expression and mindfulness practice.
            </p>
            <p className="mb-6">
              In our forest setting, the handpan's resonant tones blend with nature's own symphony, creating a deeply immersive sound experience. You don't need any musical background – the handpan is intuitive and forgiving, allowing anyone to create beautiful sounds from the first touch.
            </p>
            <p className="mb-6">
              This workshop combines technical instruction with meditative play, giving you both the skills to continue your handpan journey and the experience of music as a spiritual practice.
            </p>
          </div>
        </div>
      </section>

      {/* Workshop Structure */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            Workshop Journey
          </h2>
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Listening Circle (30 min)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Begin by listening to the handpan's voice. Experience its range of tones and how they affect your nervous system.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>First Touch & Basic Techniques (90 min)</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Proper hand positioning and striking techniques</li>
                  <li>Understanding the note layout and scale</li>
                  <li>Creating your first simple melodies</li>
                  <li>Rhythm patterns and timing</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Mindful Music Practice (60 min)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Learn to play as meditation. Let the music emerge naturally without forcing, using the handpan as a tool for presence and emotional expression.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Forest Jam Session (40 min)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Play together in a circle, creating spontaneous music that harmonizes with the forest sounds around us. Pure musical magic.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            What's Included
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Equipment & Materials</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Professional handpan for each participant</li>
                <li>• Basic music theory handout</li>
                <li>• Practice exercises to take home</li>
                <li>• Recording of the final jam session</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Experience</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Expert instruction from certified teacher</li>
                <li>• Small group setting (max 8 people)</li>
                <li>• Herbal tea and healthy snacks</li>
                <li>• Resource list for purchasing instruments</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Instructor */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            Your Guide
          </h2>
          <Card className="p-8">
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="w-32 h-32 bg-accent/20 rounded-full flex items-center justify-center">
                <span className="text-2xl font-serif">MS</span>
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-4">Marcus Soundheart</h3>
                <p className="text-muted-foreground mb-4">
                  Marcus has been playing handpan for over 8 years and teaching for 5. He discovered the instrument during his own spiritual journey and has since helped hundreds of people find their musical voice through the handpan.
                </p>
                <p className="text-muted-foreground">
                  His teaching philosophy centers on the belief that everyone is naturally musical – we just need the right instrument and environment to let our inner musician emerge.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Investment */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif font-light mb-8">Investment</h2>
          <Card className="p-8">
            <div className="text-4xl font-semibold mb-4">$125</div>
            <p className="text-muted-foreground mb-6">Per person</p>
            <div className="text-sm text-muted-foreground mb-8">
              <p>Includes handpan use, instruction, materials, refreshments</p>
              <p>Bring a friend discount: $20 off each when booking together</p>
            </div>
            <Button size="lg" className="w-full" onClick={handleBooking}>
              Reserve Your Sound Journey
            </Button>
          </Card>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
};

export default HandpanWorkshop;